import axios, {AxiosResponse} from 'axios';
import {Order, OrderStatusTrackingData, PurchaseRequest} from '../models';
import {
  OrderPurchase,
  OrderPurchaseWithSuggestions,
} from 'app/modules/purchases/Purchase.types';

const API_URL = process.env.REACT_APP_API_URL;
const GET_ORDERS_URL = `${API_URL}/orders`;
const ORDERS_STATUS_TRACKER_URL = process.env.REACT_APP_TRACKER_SERVICE_URL;

type updateOrderItemsStatusBody = {
  orderItems: Array<{uid: string; quantity: number}>;
  cancelation_reasons?: string;
  cancelation_reasons_details?: string;
  incident_event: string;
  status: string;
  itemsLocation?: string;
  mustCallLogisticsImmediately?: boolean;
};

export const getOrders = (
  query: string
): Promise<{result: Order[]; resultCount: number}> => {
  return axios
    .get(`${GET_ORDERS_URL}?${query}`)
    .then(
      (resp: AxiosResponse<{result: Order[]; resultCount: number}>) => resp.data
    );
};

export const getOrderPurchases = (
  query?: string
): Promise<{orders: OrderPurchase[]; resultCount: number}> => {
  return axios
    .get(`${GET_ORDERS_URL}/purchase?${query}`)
    .then(
      (resp: AxiosResponse<{orders: OrderPurchase[]; resultCount: number}>) =>
        resp.data
    )
    .catch((e) => {
      throw e;
    });
};

export const showOrder = (orderUid?: string): Promise<Order> => {
  return axios
    .get(`${GET_ORDERS_URL}/${orderUid}`)
    .then((resp: AxiosResponse<Order>) => resp.data)
    .catch((e) => {
      throw e;
    });
};

export const showOrderPurchase = (
  orderUid?: string
): Promise<OrderPurchaseWithSuggestions> => {
  return axios
    .get(`${GET_ORDERS_URL}/${orderUid}/purchase`)
    .then((resp: AxiosResponse<OrderPurchaseWithSuggestions>) => resp.data)
    .catch((e) => {
      throw e;
    });
};

export const pullOrderPurchasesFromQueue = ({
  priority,
}: {
  priority: boolean;
}): Promise<PurchaseRequest[]> => {
  return axios
    .patch(`${GET_ORDERS_URL}/pull?priority=${priority}`)
    .then((resp: AxiosResponse<PurchaseRequest[]>) => resp.data)
    .catch((e) => {
      throw e;
    });
};

export const transferOrderPurchaseAssignment = ({
  orderUid,
  assigned_account_user_uid,
}: {
  orderUid: string;
  assigned_account_user_uid: string | null;
}): Promise<OrderPurchase> => {
  return axios
    .patch(`${GET_ORDERS_URL}/${orderUid}/purchase-requests`, {
      assignedAccountUserUid: assigned_account_user_uid,
    })
    .then((resp: AxiosResponse<OrderPurchase>) => resp.data)
    .catch((e) => {
      throw e;
    });
};

export const transferOrderPurchasePriority = ({
  orderUid,
  priority,
}: {
  orderUid: string;
  priority: boolean;
}): Promise<OrderPurchase> => {
  return axios
    .patch(`${GET_ORDERS_URL}/${orderUid}/purchase-requests`, {
      priority,
    })
    .then((resp: AxiosResponse<OrderPurchase>) => resp.data)
    .catch((e) => {
      throw e;
    });
};

export const cancelOrderItems = (
  orderUid: string,
  body: updateOrderItemsStatusBody
): Promise<Order> => {
  return axios
    .put(`${GET_ORDERS_URL}/${orderUid}/items/cancel`, body)
    .then((resp: AxiosResponse<Order>) => resp.data);
};

export const getOrderStatusTracking = (
  referenceId: number
): Promise<OrderStatusTrackingData> => {
  return axios
    .get(`${ORDERS_STATUS_TRACKER_URL}/tracker/orders/${referenceId}`)
    .then((resp: AxiosResponse<OrderStatusTrackingData>) => resp.data);
};
